import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaUser } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { getStudentDetails } from "../../services/admin.services";
import { useSelector } from "react-redux";

function StudentDetails() {
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);
  const navigate = useNavigate();
  const { studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (!studentId) {
      setError("Student ID is missing from the URL.");
      setLoading(false);
      return;
    }

    const fetchStudentDetails = async () => {
      try {
        const response = await getStudentDetails(studentId);
        setStudent(response.data);
      } catch (error) {
        console.error("Error fetching student details:", error);
        setError(error.response?.data || "Failed to fetch student details.");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentDetails();
  }, [studentId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-24 h-24 bg-gray-200 rounded-full mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-48 mb-3"></div>
          <div className="h-3 bg-gray-200 rounded w-40 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-40 mb-2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="text-center">
          <p className="text-red-500 font-medium">
            There was an Error.Please Try again later
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded text-sm transition-colors duration-200"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="text-center">
          <p className="text-gray-600">No student details found.</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm transition-colors duration-200"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Function to get initials from username
  const getInitials = (username) => {
    if (!username) return "";
    return username.charAt(0).toUpperCase();
  };

  return (
    <div className="w-full">
      {/* Back button is already in the parent component */}
      <div className="p-4">
        <h1 className="text-lg font-semibold border-b p-4 border-purple-300 text-gray-700">
          {student.username || "N/A"}'s Application
        </h1>

        <div className="p-4">
          {/* Mobile Layout (stacked) */}
          <div className="block md:hidden">
            {/* Profile Picture - Centered on mobile */}
            <div className="flex justify-start mb-6">
              {imageError || !student.profile_picture_url ? (
                <div className="w-24 h-24 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">
                    {getInitials(student.username)}
                  </span>
                </div>
              ) : (
                <img
                  src={student.profile_picture_url}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-2 border-purple-300 shadow-sm"
                  onError={() => setImageError(true)}
                />
              )}
            </div>

            {/* Student Information - Single column on mobile */}
            <div className="space-y-4">
              <div>
                <p className="text-gray-600">
                  <strong>Full Name:</strong> {student.username || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-gray-600">
                  <strong>Email:</strong> {student.email || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-gray-600">
                  <strong>Mobile Number:</strong>{" "}
                  {student.phone_number || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-gray-600">
                  <strong>Date of Birth:</strong>{" "}
                  {student.date_of_birth || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-gray-600">
                  <strong>Gender:</strong> {student.gender || "N/A"}
                </p>
              </div>

              <div>
                <p className="text-gray-600">
                  <strong>Location:</strong> {student.city || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Desktop/Tablet Layout (side by side) */}
          <div className="hidden md:flex md:items-start">
            {/* Profile Picture */}
            <div className="flex-shrink-0 pr-8 pb-6">
              {imageError || !student.profile_picture_url ? (
                <div className="w-24 h-24 bg-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">
                    {getInitials(student.username)}
                  </span>
                </div>
              ) : (
                <img
                  src={student.profile_picture_url}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover border-2 border-purple-300 shadow-sm"
                  onError={() => setImageError(true)}
                />
              )}
            </div>

            {/* Student Information in Two Columns */}
            <div className="flex-grow grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-3">
              {/* Column 1 */}
              <div>
                <div className="mb-3">
                  <p className="text-gray-600">
                    <strong>Full Name:</strong> {student.username || "N/A"}
                  </p>
                </div>

                <div className="mb-3">
                  <p className="text-gray-600">
                    <strong>Email:</strong> {student.email || "N/A"}
                  </p>
                </div>

                <div className="mb-3">
                  <p className="text-gray-600">
                    <strong>Mobile Number:</strong>{" "}
                    {student.phone_number || "N/A"}
                  </p>
                </div>
              </div>

              {/* Column 2 */}
              <div>
                <div className="mb-3">
                  <p className="text-gray-600">
                    <strong>Date of Birth:</strong>{" "}
                    {student.date_of_birth || "N/A"}
                  </p>
                </div>

                <div className="mb-3">
                  <p className="text-gray-600">
                    <strong>Gender:</strong> {student.gender || "N/A"}
                  </p>
                </div>

                <div className="mb-3">
                  <p className="text-gray-600">
                    <strong>Location:</strong> {student.city || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentDetails;
